<template>
  <div>
    <b-card class="mb-2">
      <div class="d-flex flex-wrap align-items-center justify-content-between">
        <div>
          <h3 class="mb-25">
            Merhaba{{ displayName ? `, ${displayName}` : '' }}
          </h3>
          <p class="mb-0 text-muted">
            Ajans Bulut Qr Sys yönetim paneline hoş geldiniz. Güncel durum aşağıda özetlenmiştir.
          </p>
        </div>
        <b-button
          variant="outline-primary"
          size="sm"
          :disabled="loading"
          @click="loadDashboard"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="14"
            class="mr-50"
          />
          Yenile
        </b-button>
      </div>
    </b-card>

    <b-row>
      <b-col
        cols="12"
        sm="6"
        lg="4"
        xl="2"
      >
        <statistic-card-horizontal
          icon="BriefcaseIcon"
          :statistic="counts.customers"
          statistic-title="Müşteri"
          color="primary"
        />
      </b-col>
      <b-col
        cols="12"
        sm="6"
        lg="4"
        xl="2"
      >
        <statistic-card-horizontal
          icon="FileTextIcon"
          :statistic="counts.licenses"
          statistic-title="Lisans"
          color="info"
        />
      </b-col>
      <b-col
        cols="12"
        sm="6"
        lg="4"
        xl="2"
      >
        <statistic-card-horizontal
          icon="CheckCircleIcon"
          :statistic="counts.activeLicenses"
          statistic-title="Aktif Lisans"
          color="success"
        />
      </b-col>
      <b-col
        cols="12"
        sm="6"
        lg="4"
        xl="2"
      >
        <statistic-card-horizontal
          icon="LayersIcon"
          :statistic="counts.projects"
          statistic-title="Ürün"
          color="warning"
        />
      </b-col>
      <b-col
        cols="12"
        sm="6"
        lg="4"
        xl="2"
      >
        <statistic-card-horizontal
          icon="UsersIcon"
          :statistic="counts.users"
          statistic-title="Yönetici"
          color="secondary"
        />
      </b-col>
      <b-col
        cols="12"
        sm="6"
        lg="4"
        xl="2"
      >
        <statistic-card-horizontal
          icon="ActivityIcon"
          :statistic="counts.eventLogs"
          statistic-title="Sys Aktivite"
          color="danger"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col
        cols="12"
        lg="7"
      >
        <b-card
          no-body
          class="mb-2"
        >
          <b-card-header class="d-flex justify-content-between align-items-center">
            <b-card-title class="mb-0">
              Son Lisanslar
            </b-card-title>
            <b-button
              variant="flat-primary"
              size="sm"
              :to="{ name: 'Licences' }"
            >
              Tümü
            </b-button>
          </b-card-header>
          <template v-if="recentLicenses.length">
            <b-table
              responsive="sm"
              :fields="licenseFields"
              :items="recentLicenses"
              striped
              small
            >
              <template #cell(customer)="data">
                <div>{{ data.item.customer || '-' }}</div>
                <div
                  v-if="data.item.project"
                  class="text-muted font-small-2"
                >
                  {{ data.item.project }}
                  <span v-if="data.item.project_package"> · {{ data.item.project_package }}</span>
                </div>
              </template>
              <template #cell(edate)="data">
                <b-badge variant="light-success">
                  {{ formatDate(data.item.sdate) }}
                </b-badge>
                <span class="mx-25">-</span>
                <b-badge variant="light-danger">
                  {{ formatDate(data.item.edate) }}
                </b-badge>
              </template>
              <template #cell(status)="data">
                <b-badge
                  v-if="data.item.status === '1'"
                  variant="light-success"
                >
                  Aktif
                </b-badge>
                <b-badge
                  v-else
                  variant="light-danger"
                >
                  Pasif
                </b-badge>
              </template>
              <template #cell(control)="data">
                <div class="text-right">
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    :to="{ name: 'LicencesEdit', params: { id: data.item.id } }"
                  >
                    Görüntüle
                  </b-button>
                </div>
              </template>
            </b-table>
          </template>
          <b-alert
            v-else
            show
            variant="info"
            class="m-2"
          >
            <div class="alert-body text-center">
              Lisans kaydı bulunamadı.
            </div>
          </b-alert>
        </b-card>

        <b-card
          no-body
          class="mb-2"
        >
          <b-card-header class="d-flex justify-content-between align-items-center">
            <b-card-title class="mb-0">
              Süresi Yaklaşan Lisanslar
              <small class="text-muted font-small-2 d-block">
                Önümüzdeki 30 gün
              </small>
            </b-card-title>
          </b-card-header>
          <template v-if="expiringLicenses.length">
            <b-table
              responsive="sm"
              :fields="expiringFields"
              :items="expiringLicenses"
              striped
              small
            >
              <template #cell(customer)="data">
                {{ data.item.customer || '-' }}
              </template>
              <template #cell(edate)="data">
                <b-badge variant="light-warning">
                  {{ formatDate(data.item.edate) }}
                </b-badge>
                <div class="text-muted font-small-2">
                  {{ daysLeft(data.item.edate) }}
                </div>
              </template>
              <template #cell(control)="data">
                <div class="text-right">
                  <b-button
                    size="sm"
                    variant="outline-warning"
                    :to="{ name: 'LicencesEdit', params: { id: data.item.id } }"
                  >
                    İncele
                  </b-button>
                </div>
              </template>
            </b-table>
          </template>
          <b-alert
            v-else
            show
            variant="success"
            class="m-2"
          >
            <div class="alert-body text-center">
              Önümüzdeki 30 gün içinde süresi dolacak lisans yok.
            </div>
          </b-alert>
        </b-card>
      </b-col>

      <b-col
        cols="12"
        lg="5"
      >
        <b-card
          no-body
          class="mb-2"
        >
          <b-card-header class="d-flex justify-content-between align-items-center">
            <b-card-title class="mb-0">
              Son Sys Aktiviteleri
            </b-card-title>
            <b-button
              variant="flat-primary"
              size="sm"
              :to="{ name: 'EventLogs' }"
            >
              Tümü
            </b-button>
          </b-card-header>
          <template v-if="recentEvents.length">
            <b-table
              responsive="sm"
              :fields="eventFields"
              :items="recentEvents"
              striped
              small
            >
              <template #cell(action)="data">
                <b-badge
                  v-if="data.item.action === 'created'"
                  variant="light-primary"
                >
                  Oluşturma
                </b-badge>
                <b-badge
                  v-else-if="data.item.action === 'modified'"
                  variant="light-warning"
                >
                  Düzenleme
                </b-badge>
                <b-badge
                  v-else-if="data.item.action === 'deleted'"
                  variant="light-danger"
                >
                  Silme
                </b-badge>
                <b-badge
                  v-else
                  variant="light-secondary"
                >
                  {{ data.item.action || '-' }}
                </b-badge>
                <div class="text-muted font-small-2">
                  {{ formatDateTime(data.item.created) }}
                </div>
              </template>
            </b-table>
          </template>
          <b-alert
            v-else
            show
            variant="info"
            class="m-2"
          >
            <div class="alert-body text-center">
              Aktivite kaydı bulunamadı.
            </div>
          </b-alert>
        </b-card>

        <b-card title="Hızlı İşlemler">
          <div class="d-flex flex-wrap">
            <b-button
              class="mr-1 mb-1"
              variant="primary"
              :to="{ name: 'CustomerAdd' }"
            >
              Müşteri Oluştur
            </b-button>
            <b-button
              class="mr-1 mb-1"
              variant="info"
              :to="{ name: 'LicencesAdd' }"
            >
              Lisans Oluştur
            </b-button>
            <b-button
              class="mr-1 mb-1"
              variant="outline-secondary"
              :to="{ name: 'Customers' }"
            >
              Müşteriler
            </b-button>
            <b-button
              class="mb-1"
              variant="outline-secondary"
              :to="{ name: 'Products' }"
            >
              Ürünler
            </b-button>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <div
      v-if="loading"
      class="text-center text-muted py-1"
    >
      Veriler yükleniyor...
    </div>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCardHeader,
  BCardTitle,
  BCol,
  BRow,
  BTable,
} from 'bootstrap-vue'
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue'

export default {
  name: 'Manager',
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCardHeader,
    BCardTitle,
    BCol,
    BRow,
    BTable,
    StatisticCardHorizontal,
  },
  data() {
    return {
      licenseFields: [
        { key: 'customer', label: 'Firma' },
        { key: 'edate', label: 'Süre', thStyle: { width: '220px' } },
        { key: 'status', label: '', thStyle: { width: '90px' } },
        { key: 'control', label: '', thStyle: { width: '110px' } },
      ],
      expiringFields: [
        { key: 'customer', label: 'Firma' },
        { key: 'edate', label: 'Bitiş', thStyle: { width: '140px' } },
        { key: 'control', label: '', thStyle: { width: '100px' } },
      ],
      eventFields: [
        { key: 'username', label: 'Kullanıcı' },
        { key: 'component', label: 'Modül' },
        { key: 'action', label: 'İşlem', thStyle: { width: '140px' } },
      ],
    }
  },
  computed: {
    loading() {
      return this.$store.getters['dashboard/loading']
    },
    counts() {
      return this.$store.getters['dashboard/counts']
    },
    recentLicenses() {
      return this.$store.getters['dashboard/recentLicenses']
    },
    expiringLicenses() {
      return this.$store.getters['dashboard/expiringLicenses']
    },
    recentEvents() {
      return this.$store.getters['dashboard/recentEvents']
    },
    displayName() {
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || 'null')
        return (userData && (userData.fullName || userData.username || userData.name)) || ''
      } catch (e) {
        return ''
      }
    },
  },
  created() {
    this.loadDashboard()
  },
  methods: {
    loadDashboard() {
      this.$store.dispatch('dashboard/loadOverview')
    },
    formatDate(value) {
      if (!value) return '-'
      return this.moment(value).format('DD.MM.YYYY')
    },
    formatDateTime(value) {
      if (!value) return '-'
      return this.moment(value).format('DD.MM.YYYY HH:mm')
    },
    daysLeft(value) {
      if (!value) return ''
      const end = this.moment(value).startOf('day')
      const today = this.moment().startOf('day')
      const diff = end.diff(today, 'days')
      if (diff === 0) return 'Bugün bitiyor'
      if (diff === 1) return '1 gün kaldı'
      return `${diff} gün kaldı`
    },
  },
}
</script>
